import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Plus,
  Trash2,
  Save,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  CheckCircle2,
  Info,
  Layers,
  Target,
  TrendingUp,
  RefreshCw,
  GripVertical,
  Eye,
  EyeOff,
  Copy,
  Zap,
  HelpCircle,
  Search,
  X,
  Filter,
  BarChart2,
  Users,
  Calendar,
  Phone,
  Star,
  Briefcase,
  Wrench,
  ChevronRight,
  Settings,
  SlidersHorizontal,
  ArrowUpDown,
  Check,
  Sparkles,
  Clock,
  Hash,
  Activity,
} from "lucide-react";
import apiService from "../services/api";
import { useTheme } from "../contexts/ThemeContext";

// ─── Metric Library ──────────────────────────────────────────────────────────

const METRIC_LIBRARY = [
  // ── Appointment metrics ──
  {
    id: "appointments_created",
    label: "Appointments Booked",
    description: "Appointments this person created/booked themselves",
    queryType: "appointments_created",
    icon: "📅",
    category: "Appointments",
    unit: "appts",
    defaultTarget: 3,
    applicableTeamTypes: [
      "sales",
      "telemarketing",
      "sales_engagement",
      "custom",
    ],
    filters: [
      {
        key: "salesType",
        label: "Sale Type",
        type: "multiselect",
        options: [
          { value: "new_business", label: "New Business" },
          { value: "base", label: "Base" },
        ],
      },
      {
        key: "saleOriginated",
        label: "Origin",
        type: "multiselect",
        options: [
          { value: "cold_call", label: "Cold Call" },
          { value: "email", label: "Email" },
          { value: "incoming_call", label: "Incoming Call" },
          { value: "website_enquiry", label: "Website Enquiry" },
          { value: "reschedule", label: "Reschedule" },
          { value: "base", label: "Base" },
        ],
      },
    ],
  },
  {
    id: "appointments_assigned",
    label: "Appointments Assigned",
    description: "Appointments assigned to this person by telemarketing/ops",
    queryType: "appointments_assigned",
    icon: "📋",
    category: "Appointments",
    unit: "appts",
    defaultTarget: 4,
    applicableTeamTypes: ["sales", "sales_engagement", "custom"],
    filters: [
      {
        key: "salesType",
        label: "Sale Type",
        type: "multiselect",
        options: [
          { value: "new_business", label: "New Business" },
          { value: "base", label: "Base" },
        ],
      },
      {
        key: "status",
        label: "Status",
        type: "multiselect",
        options: [
          { value: "scheduled", label: "Scheduled" },
          { value: "confirmed", label: "Confirmed" },
          { value: "completed", label: "Completed" },
        ],
      },
    ],
  },
  {
    id: "appointments_completed",
    label: "Appointments Attended",
    description: "Appointments marked as completed/attended",
    queryType: "appointments_completed",
    icon: "✅",
    category: "Appointments",
    unit: "appts",
    defaultTarget: 3,
    applicableTeamTypes: ["sales", "engineering", "sales_engagement", "custom"],
    filters: [
      {
        key: "installationBookedAs",
        label: "Booked As",
        type: "multiselect",
        options: [
          { value: "new_install", label: "New Install" },
          { value: "base_install", label: "Base Install" },
          { value: "revisit_for_support", label: "Revisit (Support)" },
          { value: "broadband_connection", label: "Broadband" },
          { value: "webex_migration", label: "Webex Migration" },
          { value: "revisit_install_problem", label: "Revisit (Problem)" },
        ],
      },
      {
        key: "installationOutcome",
        label: "Outcome",
        type: "multiselect",
        options: [
          { value: "new_install", label: "New Install" },
          { value: "base_install", label: "Base Install" },
          { value: "revisit_for_support", label: "Revisit (Support)" },
          { value: "broadband_connection", label: "Broadband" },
          { value: "webex_migration", label: "Webex Migration" },
          { value: "revisit_install_problem", label: "Revisit (Problem)" },
        ],
      },
    ],
  },
  {
    id: "appointments_cancelled",
    label: "Appointments Cancelled",
    description: "Appointments that were cancelled",
    queryType: "appointments_cancelled",
    icon: "❌",
    category: "Appointments",
    unit: "appts",
    defaultTarget: 2,
    applicableTeamTypes: [
      "sales",
      "telemarketing",
      "engineering",
      "sales_engagement",
      "custom",
    ],
    filters: [
      {
        key: "salesType",
        label: "Sale Type",
        type: "multiselect",
        options: [
          { value: "new_business", label: "New Business" },
          { value: "base", label: "Base" },
        ],
      },
    ],
  },
  {
    id: "appointments_no_show",
    label: "Not Sat",
    description: "Appointments not sat",
    queryType: "appointments_no_show",
    icon: "👻",
    category: "Appointments",
    unit: "appts",
    defaultTarget: 1,
    applicableTeamTypes: ["sales", "engineering", "sales_engagement", "custom"],
    filters: [],
  },
  // ── Lead metrics ──
  {
    id: "leads_created",
    label: "Leads Created",
    description: "New lead records created by this person",
    queryType: "leads_created",
    icon: "👤",
    category: "Leads",
    unit: "leads",
    defaultTarget: 10,
    applicableTeamTypes: [
      "sales",
      "telemarketing",
      "sales_engagement",
      "custom",
    ],
    filters: [
      {
        key: "status",
        label: "Initial Status",
        type: "multiselect",
        options: [
          { value: "new", label: "New" },
          { value: "contacted", label: "Contacted" },
        ],
      },
      {
        key: "leadSource",
        label: "Lead Source",
        type: "multiselect",
        options: [
          { value: "Web", label: "Web" },
          { value: "Phone", label: "Phone" },
          { value: "Email", label: "Email" },
          { value: "Referral", label: "Referral" },
          { value: "Cold Call", label: "Cold Call" },
        ],
      },
    ],
  },
  {
    id: "leads_qualified",
    label: "Leads Qualified",
    description: "Leads moved to 'Qualified' status",
    queryType: "leads_qualified",
    icon: "⭐",
    category: "Leads",
    unit: "leads",
    defaultTarget: 5,
    applicableTeamTypes: [
      "sales",
      "telemarketing",
      "sales_engagement",
      "custom",
    ],
    filters: [],
  },
  {
    id: "leads_contacted",
    label: "Leads Contacted",
    description: "Leads where first contact has been made",
    queryType: "leads_contacted",
    icon: "📬",
    category: "Leads",
    unit: "leads",
    defaultTarget: 15,
    applicableTeamTypes: [
      "sales",
      "telemarketing",
      "sales_engagement",
      "custom",
    ],
    filters: [],
  },
  {
    id: "leads_converted",
    label: "Leads Converted",
    description: "Leads successfully converted to opportunities or accounts",
    queryType: "leads_converted",
    icon: "🔄",
    category: "Leads",
    unit: "leads",
    defaultTarget: 3,
    applicableTeamTypes: ["sales", "sales_engagement", "custom"],
    filters: [],
  },
  // ── Opportunity metrics ──
  {
    id: "opportunities_created",
    label: "Opportunities Created",
    description: "New opportunities opened by this person",
    queryType: "opportunities_created",
    icon: "💼",
    category: "Opportunities",
    unit: "opps",
    defaultTarget: 2,
    applicableTeamTypes: ["sales", "sales_engagement", "custom"],
    filters: [
      {
        key: "stage",
        label: "Stage",
        type: "multiselect",
        options: [
          { value: "qualification", label: "Qualification" },
          { value: "needs_analysis", label: "Needs Analysis" },
          { value: "proposal", label: "Proposal" },
          { value: "negotiation", label: "Negotiation" },
        ],
      },
      {
        key: "salesType",
        label: "Sale Type",
        type: "multiselect",
        options: [
          { value: "new_business", label: "New Business" },
          { value: "base", label: "Base" },
        ],
      },
    ],
  },
  {
    id: "opportunities_sold",
    label: "Deals Won",
    description: "Opportunities sold",
    queryType: "opportunities_sold",
    icon: "🏆",
    category: "Opportunities",
    unit: "deals",
    defaultTarget: 1,
    applicableTeamTypes: ["sales", "sales_engagement", "custom"],
    filters: [
      {
        key: "salesType",
        label: "Sale Type",
        type: "multiselect",
        options: [
          { value: "new_business", label: "New Business" },
          { value: "base", label: "Base" },
        ],
      },
    ],
  },
  {
    id: "opportunities_sat_qual",
    label: "Sat Qual",
    description: "Opportunities with 'Sat Qualified' sales status",
    queryType: "opportunities_sat_qual",
    icon: "🎯",
    category: "Opportunities",
    unit: "opps",
    defaultTarget: 2,
    applicableTeamTypes: [
      "telemarketing",
      "sales",
      "sales_engagement",
      "custom",
    ],
    filters: [],
  },
  {
    id: "opportunities_sat_nonqual",
    label: "Sat Non-Qual",
    description: "Opportunities with 'Sat Non-Qualified' sales status",
    queryType: "opportunities_sat_nonqual",
    icon: "📊",
    category: "Opportunities",
    unit: "opps",
    defaultTarget: 1,
    applicableTeamTypes: ["sales", "sales_engagement", "custom"],
    filters: [],
  },
  // ── Case metrics ──
  {
    id: "cases_created",
    label: "Cases Opened",
    description: "New support cases created/owned by this person",
    queryType: "cases_created",
    icon: "📂",
    category: "Cases",
    unit: "cases",
    defaultTarget: 5,
    applicableTeamTypes: ["support", "operations", "custom"],
    filters: [
      {
        key: "priority",
        label: "Priority",
        type: "multiselect",
        options: [
          { value: "High", label: "High" },
          { value: "Medium", label: "Medium" },
          { value: "Low", label: "Low" },
        ],
      },
      {
        key: "origin",
        label: "Origin",
        type: "multiselect",
        options: [
          { value: "Email", label: "Email" },
          { value: "Phone", label: "Phone" },
          { value: "Web", label: "Web" },
        ],
      },
    ],
  },
  {
    id: "cases_resolved",
    label: "Cases Resolved",
    description: "Support cases closed/resolved by this person",
    queryType: "cases_resolved",
    icon: "✔️",
    category: "Cases",
    unit: "cases",
    defaultTarget: 8,
    applicableTeamTypes: ["support", "operations", "engineering", "custom"],
    filters: [
      {
        key: "type",
        label: "Case Type",
        type: "multiselect",
        options: [
          { value: "Technical Fault", label: "Technical Fault" },
          { value: "Technical Fault Line 1", label: "Tech Fault L1" },
          { value: "Technical Fault Line 2", label: "Tech Fault L2" },
          { value: "Install Problems", label: "Install Problems" },
          { value: "Billing Query", label: "Billing Query" },
          { value: "Porting", label: "Porting" },
          { value: "Revisit Required", label: "Revisit Required" },
          { value: "Permanent Cease", label: "Permanent Cease" },
          { value: "CCTV Fault", label: "CCTV Fault" },
        ],
      },
      {
        key: "priority",
        label: "Priority",
        type: "multiselect",
        options: [
          { value: "High", label: "High" },
          { value: "Medium", label: "Medium" },
          { value: "Low", label: "Low" },
        ],
      },
    ],
  },
  {
    id: "cases_contacted",
    label: "Cases Contacted",
    description: "Cases where first contact was made within SLA window",
    queryType: "cases_contacted",
    icon: "🔔",
    category: "Cases",
    unit: "cases",
    defaultTarget: 10,
    applicableTeamTypes: ["support", "operations", "custom"],
    filters: [],
  },
  // ── Installation metrics ──
  {
    id: "installations_completed",
    label: "Installations Completed",
    description: "Installation appointments completed by outcome type",
    queryType: "appointments_completed",
    icon: "🔧",
    category: "Installations",
    unit: "installs",
    defaultTarget: 3,
    applicableTeamTypes: ["engineering", "operations", "custom"],
    filters: [
      {
        key: "installationOutcome",
        label: "Outcome Type",
        type: "multiselect",
        options: [
          { value: "new_install", label: "New Install" },
          { value: "base_install", label: "Base Install" },
          { value: "revisit_for_support", label: "Revisit (Support)" },
          { value: "broadband_connection", label: "Broadband" },
          { value: "webex_migration", label: "Webex Migration" },
          { value: "revisit_install_problem", label: "Revisit (Problem)" },
        ],
      },
    ],
  },
];

const CATEGORY_META = {
  Appointments: {
    icon: Calendar,
    color: "text-blue-400",
    bg: "bg-blue-500/10",
  },
  Leads: { icon: Users, color: "text-purple-400", bg: "bg-purple-500/10" },
  Opportunities: {
    icon: Briefcase,
    color: "text-amber-400",
    bg: "bg-amber-500/10",
  },
  Cases: { icon: Activity, color: "text-red-400", bg: "bg-red-500/10" },
  Installations: {
    icon: Wrench,
    color: "text-emerald-400",
    bg: "bg-emerald-500/10",
  },
  Activity: { icon: Phone, color: "text-cyan-400", bg: "bg-cyan-500/10" },
};

const TEAM_TYPE_LABELS = {
  sales: "Sales",
  sales_engagement: "Sales Engagement",
  telemarketing: "Telemarketing",
  support: "Support",
  operations: "Operations",
  engineering: "Engineering",
  management: "Management",
  hr: "HR",
  finance: "Finance",
  product: "Product",
  custom: "Custom",
};

const PRESET_CONFIGS = [
  {
    id: "telemarketing_standard",
    name: "Telemarketing",
    description: "Appointments booked + leads qualified + calls made",
    icon: "📞",
    teamTypes: ["telemarketing"],
    metrics: [
      {
        metricId: "appointments_created",
        label: "Appointments Booked",
        filters: {},
        weeklyDefaults: {
          monday: 5,
          tuesday: 5,
          wednesday: 5,
          thursday: 5,
          friday: 3,
          saturday: 0,
          sunday: 0,
        },
        enabled: true,
        primaryMetric: true,
      },
      {
        metricId: "leads_created",
        label: "Leads Added",
        filters: {},
        weeklyDefaults: {
          monday: 10,
          tuesday: 10,
          wednesday: 10,
          thursday: 10,
          friday: 8,
          saturday: 0,
          sunday: 0,
        },
        enabled: true,
        primaryMetric: false,
      },
      {
        metricId: "calls_made",
        label: "Calls Made",
        filters: {},
        weeklyDefaults: {
          monday: 40,
          tuesday: 40,
          wednesday: 40,
          thursday: 40,
          friday: 30,
          saturday: 0,
          sunday: 0,
        },
        enabled: true,
        primaryMetric: false,
      },
    ],
  },
  {
    id: "sales_standard",
    name: "Sales",
    description: "Appointments assigned/attended + deals won",
    icon: "💼",
    teamTypes: ["sales", "sales_engagement"],
    metrics: [
      {
        metricId: "appointments_assigned",
        label: "Appointments Assigned",
        filters: {},
        weeklyDefaults: {
          monday: 4,
          tuesday: 4,
          wednesday: 4,
          thursday: 4,
          friday: 2,
          saturday: 0,
          sunday: 0,
        },
        enabled: true,
        primaryMetric: true,
      },
      {
        metricId: "appointments_completed",
        label: "Appointments Attended",
        filters: {},
        weeklyDefaults: {
          monday: 3,
          tuesday: 3,
          wednesday: 3,
          thursday: 3,
          friday: 2,
          saturday: 0,
          sunday: 0,
        },
        enabled: true,
        primaryMetric: false,
      },
      {
        metricId: "opportunities_sat_qual",
        label: "Sat Qual",
        filters: {},
        weeklyDefaults: {
          monday: 2,
          tuesday: 2,
          wednesday: 2,
          thursday: 2,
          friday: 1,
          saturday: 0,
          sunday: 0,
        },
        enabled: true,
        primaryMetric: false,
      },
      {
        metricId: "opportunities_closed_won",
        label: "Deals Won",
        filters: {},
        weeklyDefaults: {
          monday: 1,
          tuesday: 1,
          wednesday: 1,
          thursday: 1,
          friday: 1,
          saturday: 0,
          sunday: 0,
        },
        enabled: true,
        primaryMetric: false,
      },
    ],
  },
  {
    id: "engineering_standard",
    name: "Engineering",
    description: "Installations completed per day by type",
    icon: "🔧",
    teamTypes: ["engineering"],
    metrics: [
      {
        metricId: "installations_completed",
        label: "Installations Done",
        filters: {},
        weeklyDefaults: {
          monday: 3,
          tuesday: 3,
          wednesday: 3,
          thursday: 3,
          friday: 3,
          saturday: 2,
          sunday: 0,
        },
        enabled: true,
        primaryMetric: true,
      },
      {
        metricId: "cases_resolved",
        label: "Cases Resolved",
        filters: { type: ["Technical Fault", "Install Problems"] },
        weeklyDefaults: {
          monday: 2,
          tuesday: 2,
          wednesday: 2,
          thursday: 2,
          friday: 2,
          saturday: 0,
          sunday: 0,
        },
        enabled: true,
        primaryMetric: false,
      },
    ],
  },
  {
    id: "support_standard",
    name: "Support",
    description: "Cases resolved + contacted on time",
    icon: "🎧",
    teamTypes: ["support"],
    metrics: [
      {
        metricId: "cases_resolved",
        label: "Cases Resolved",
        filters: {},
        weeklyDefaults: {
          monday: 8,
          tuesday: 8,
          wednesday: 8,
          thursday: 8,
          friday: 6,
          saturday: 0,
          sunday: 0,
        },
        enabled: true,
        primaryMetric: true,
      },
      {
        metricId: "cases_contacted",
        label: "Initial Contact Made",
        filters: {},
        weeklyDefaults: {
          monday: 10,
          tuesday: 10,
          wednesday: 10,
          thursday: 10,
          friday: 8,
          saturday: 0,
          sunday: 0,
        },
        enabled: true,
        primaryMetric: false,
      },
      {
        metricId: "calls_made",
        label: "Calls Made",
        filters: {},
        weeklyDefaults: {
          monday: 20,
          tuesday: 20,
          wednesday: 20,
          thursday: 20,
          friday: 15,
          saturday: 0,
          sunday: 0,
        },
        enabled: true,
        primaryMetric: false,
      },
    ],
  },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

const makeMetricEntry = (metricDef) => ({
  id: `metric_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
  metricId: metricDef.id,
  label: metricDef.label,
  description: metricDef.description,
  queryType: metricDef.queryType,
  unit: metricDef.unit,
  icon: metricDef.icon,
  category: metricDef.category,
  filters: {},
  weeklyDefaults: {
    monday: metricDef.defaultTarget,
    tuesday: metricDef.defaultTarget,
    wednesday: metricDef.defaultTarget,
    thursday: metricDef.defaultTarget,
    friday: metricDef.defaultTarget,
    saturday: 0,
    sunday: 0,
  },
  enabled: true,
  primaryMetric: false,
});

const DAYS = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];
const DAY_SHORT = {
  monday: "Mon",
  tuesday: "Tue",
  wednesday: "Wed",
  thursday: "Thu",
  friday: "Fri",
  saturday: "Sat",
  sunday: "Sun",
};

// ─── Tooltip ──────────────────────────────────────────────────────────────────

const Tooltip = ({ text, children, side = "top" }) => (
  <span className="relative group inline-flex">
    {children}
    <span
      className={`absolute ${side === "top" ? "bottom-full left-1/2 -translate-x-1/2 mb-2" : "top-full left-1/2 -translate-x-1/2 mt-2"} hidden group-hover:flex bg-gray-900 text-white text-xs rounded-lg px-3 py-2 whitespace-nowrap shadow-xl z-[60] border border-gray-700 max-w-xs text-center pointer-events-none`}
    >
      {text}
    </span>
  </span>
);

// ─── Badge ────────────────────────────────────────────────────────────────────

const Badge = ({ children, color = "blue", size = "sm" }) => {
  const colors = {
    blue: "bg-blue-500/15 text-blue-400 border-blue-500/30",
    green: "bg-green-500/15 text-green-400 border-green-500/30",
    yellow: "bg-yellow-500/15 text-yellow-400 border-yellow-500/30",
    gray: "bg-gray-500/15 text-gray-400 border-gray-500/30",
    purple: "bg-purple-500/15 text-purple-400 border-purple-500/30",
    amber: "bg-amber-500/15 text-amber-400 border-amber-500/30",
    red: "bg-red-500/15 text-red-400 border-red-500/30",
    emerald: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  };
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${colors[color]}`}
    >
      {children}
    </span>
  );
};

// ─── MetricCard ───────────────────────────────────────────────────────────────

const MetricCard = ({
  metric,
  index,
  total,
  onUpdate,
  onRemove,
  onTogglePrimary,
  onMoveUp,
  onMoveDown,
  isDarkMode,
}) => {
  const [expanded, setExpanded] = useState(true);
  const metricDef = METRIC_LIBRARY.find((m) => m.id === metric.metricId);
  const categoryMeta =
    CATEGORY_META[metricDef?.category] || CATEGORY_META["Activity"];

  const cardBg = isDarkMode
    ? "bg-[#0D0F14] border-gray-700/80"
    : "bg-white border-gray-200";
  const inputBg = isDarkMode
    ? "bg-[#1A1D23] border-gray-600 text-white placeholder-gray-500"
    : "bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-400";
  const labelCls = isDarkMode ? "text-gray-300" : "text-gray-700";
  const mutedCls = isDarkMode ? "text-gray-500" : "text-gray-400";
  const sectionBg = isDarkMode
    ? "bg-white/[0.03] border-gray-700/50"
    : "bg-gray-50 border-gray-200";

  const weeklyTotal = DAYS.filter((d) => metric.weeklyDefaults[d] > 0).reduce(
    (s, d) => s + (metric.weeklyDefaults[d] || 0),
    0,
  );

  const updateDefault = (day, val) => {
    const n = Math.max(0, Math.min(999, parseInt(val) || 0));
    onUpdate({
      ...metric,
      weeklyDefaults: { ...metric.weeklyDefaults, [day]: n },
    });
  };

  const toggleFilter = (filterKey, optValue) => {
    const current = metric.filters[filterKey] || [];
    const next = current.includes(optValue)
      ? current.filter((v) => v !== optValue)
      : [...current, optValue];
    onUpdate({
      ...metric,
      filters: {
        ...metric.filters,
        [filterKey]: next.length ? next : undefined,
      },
    });
  };

  const applyQuickFill = (pattern) => {
    const avg =
      Math.round(
        ["monday", "tuesday", "wednesday", "thursday", "friday"].reduce(
          (s, d) => s + (metric.weeklyDefaults[d] || 0),
          0,
        ) / 5,
      ) ||
      metricDef?.defaultTarget ||
      3;

    const patterns = {
      weekdays: {
        monday: avg,
        tuesday: avg,
        wednesday: avg,
        thursday: avg,
        friday: avg,
        saturday: 0,
        sunday: 0,
      },
      "all-days": Object.fromEntries(DAYS.map((d) => [d, avg])),
      "mon-thu": {
        monday: avg,
        tuesday: avg,
        wednesday: avg,
        thursday: avg,
        friday: Math.max(0, avg - 1),
        saturday: 0,
        sunday: 0,
      },
      reset: {
        monday: metricDef?.defaultTarget || 3,
        tuesday: metricDef?.defaultTarget || 3,
        wednesday: metricDef?.defaultTarget || 3,
        thursday: metricDef?.defaultTarget || 3,
        friday: metricDef?.defaultTarget || 3,
        saturday: 0,
        sunday: 0,
      },
    };
    onUpdate({ ...metric, weeklyDefaults: patterns[pattern] });
  };

  const activeFilterCount = Object.values(metric.filters || {}).filter(
    (v) => v && (Array.isArray(v) ? v.length > 0 : true),
  ).length;

  return (
    <div
      className={`rounded-xl border transition-all duration-200 ${cardBg} ${metric.primaryMetric ? "ring-2 ring-[#427BCA]/40 shadow-[0_0_20px_rgba(66,123,202,0.08)]" : ""} ${!metric.enabled ? "opacity-60" : ""}`}
    >
      {/* Card header */}
      <div className="flex items-center gap-3 p-4">
        {/* Reorder */}
        <div className="flex flex-col gap-0.5 flex-shrink-0">
          <button
            onClick={() => onMoveUp(index)}
            disabled={index === 0}
            className={`p-0.5 rounded transition-colors ${index === 0 ? "opacity-20 cursor-not-allowed" : isDarkMode ? "text-gray-500 hover:text-gray-300" : "text-gray-400 hover:text-gray-600"}`}
          >
            <ChevronUp size={12} />
          </button>
          <button
            onClick={() => onMoveDown(index)}
            disabled={index === total - 1}
            className={`p-0.5 rounded transition-colors ${index === total - 1 ? "opacity-20 cursor-not-allowed" : isDarkMode ? "text-gray-500 hover:text-gray-300" : "text-gray-400 hover:text-gray-600"}`}
          >
            <ChevronDown size={12} />
          </button>
        </div>

        {/* Category colour dot + icon */}
        <div
          className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 text-lg ${categoryMeta.bg}`}
        >
          {metric.icon}
        </div>

        <div className="flex-1 min-w-0">
          <input
            value={metric.label}
            onChange={(e) => onUpdate({ ...metric, label: e.target.value })}
            className={`w-full bg-transparent font-semibold text-sm focus:outline-none border-b border-transparent focus:border-[#427BCA] transition-colors pb-0.5 ${labelCls}`}
            placeholder="Metric name…"
          />
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            <span className={`text-xs ${mutedCls}`}>
              {metricDef?.description}
            </span>
            {activeFilterCount > 0 && (
              <Badge color="blue">
                <Filter size={9} />
                {activeFilterCount} filter{activeFilterCount > 1 ? "s" : ""}
              </Badge>
            )}
            <Badge color="gray">
              <Hash size={9} />
              {weeklyTotal}/wk
            </Badge>
          </div>
        </div>

        <div className="flex items-center gap-1 flex-shrink-0">
          {metric.primaryMetric && (
            <Badge color="blue">
              <Zap size={9} />
              Primary
            </Badge>
          )}

          <Tooltip
            text={
              metric.primaryMetric
                ? "Remove as primary"
                : "Set as primary metric (shown front-and-centre on dashboards)"
            }
          >
            <button
              onClick={() => onTogglePrimary(metric.id)}
              className={`p-1.5 rounded-lg transition-colors ${metric.primaryMetric ? "text-[#427BCA] bg-[#427BCA]/10" : isDarkMode ? "text-gray-500 hover:text-gray-300" : "text-gray-400 hover:text-gray-600"}`}
            >
              <Zap size={13} />
            </button>
          </Tooltip>

          <Tooltip
            text={metric.enabled ? "Pause this metric" : "Resume this metric"}
          >
            <button
              onClick={() => onUpdate({ ...metric, enabled: !metric.enabled })}
              className={`p-1.5 rounded-lg transition-colors ${metric.enabled ? "text-green-400" : isDarkMode ? "text-gray-600" : "text-gray-300"}`}
            >
              {metric.enabled ? <Eye size={13} /> : <EyeOff size={13} />}
            </button>
          </Tooltip>

          <button
            onClick={() => setExpanded((e) => !e)}
            className={`p-1.5 rounded-lg transition-colors ${isDarkMode ? "text-gray-500 hover:text-gray-300" : "text-gray-400 hover:text-gray-600"}`}
          >
            {expanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
          </button>

          <button
            onClick={() => onRemove(metric.id)}
            className="p-1.5 rounded-lg text-red-400/60 hover:text-red-400 hover:bg-red-500/10 transition-colors"
          >
            <Trash2 size={13} />
          </button>
        </div>
      </div>

      {/* Paused warning */}
      {!metric.enabled && (
        <div className="mx-4 mb-3 px-3 py-2 rounded-lg bg-yellow-500/10 border border-yellow-500/20 text-yellow-400 text-xs flex items-center gap-2">
          <AlertCircle size={12} />
          Paused — members won't see targets for this metric
        </div>
      )}

      {expanded && (
        <div className="px-4 pb-4 space-y-4">
          {/* ── Daily targets ── */}
          <GranularityTabs
            metric={metric}
            onChange={onUpdate}
            isDarkMode={isDarkMode}
            labelCls={labelCls}
            mutedCls={mutedCls}
            sectionBg={sectionBg}
            inputBg={inputBg}
            metricDef={metricDef}
            applyQuickFill={applyQuickFill}
            updateDefault={updateDefault}
          />

          {/* ── Filters ── */}
          {metricDef?.filters && metricDef.filters.length > 0 && (
            <div className={`rounded-lg border p-3 ${sectionBg}`}>
              <div className="flex items-center gap-2 mb-3">
                <span
                  className={`text-xs font-semibold uppercase tracking-wider ${mutedCls}`}
                >
                  Filters
                </span>
                <Badge color="gray">optional</Badge>
                <Tooltip text="Narrow which records count. Leave blank to count all records of this type.">
                  <HelpCircle size={11} className={mutedCls} />
                </Tooltip>
              </div>
              <div className="space-y-3">
                {metricDef.filters.map((filter) => {
                  const selected = metric.filters[filter.key] || [];
                  return (
                    <div key={filter.key}>
                      <div className="flex items-center justify-between mb-1.5">
                        <p className={`text-xs font-medium ${labelCls}`}>
                          {filter.label}
                        </p>
                        {selected.length > 0 && (
                          <button
                            onClick={() =>
                              onUpdate({
                                ...metric,
                                filters: {
                                  ...metric.filters,
                                  [filter.key]: undefined,
                                },
                              })
                            }
                            className="text-xs text-gray-500 hover:text-red-400 flex items-center gap-0.5 transition-colors"
                          >
                            <X size={10} />
                            Clear
                          </button>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {filter.options.map((opt) => {
                          const active = selected.includes(opt.value);
                          return (
                            <button
                              key={opt.value}
                              onClick={() =>
                                toggleFilter(filter.key, opt.value)
                              }
                              className={`text-xs px-2.5 py-1 rounded-full border transition-all flex items-center gap-1 ${
                                active
                                  ? "bg-[#427BCA]/20 border-[#427BCA] text-[#427BCA]"
                                  : isDarkMode
                                    ? "border-gray-700 text-gray-400 hover:border-gray-500 hover:text-gray-300"
                                    : "border-gray-300 text-gray-500 hover:border-gray-400"
                              }`}
                            >
                              {active && <Check size={9} />}
                              {opt.label}
                            </button>
                          );
                        })}
                      </div>
                      {selected.length === 0 && (
                        <p className={`text-xs mt-1 ${mutedCls}`}>
                          All {filter.label.toLowerCase()}s counted
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
const GranularityTabs = ({
  metric,
  onChange,
  isDarkMode,
  labelCls,
  mutedCls,
  sectionBg,
  inputBg,
  metricDef,
  applyQuickFill,
  updateDefault,
}) => {
  const [tab, setTab] = useState(metric.granularity || "daily");
  const periodType = metric.periodType || "weekday_pattern";

  const setGranularity = (g) => {
    setTab(g);
    onChange({ ...metric, granularity: g });
  };

  const setPeriodType = (pt) => {
    onChange({ ...metric, periodType: pt });
  };

  // Live preview
  const preview = (() => {
    if (periodType === "fixed" && metric.fixedValue != null) {
      return `This sets ~${metric.fixedValue} ${metric.unit}/${tab.replace("ly", "")}`;
    }
    const total = DAYS.filter((d) => metric.weeklyDefaults?.[d] > 0).reduce(
      (s, d) => s + (metric.weeklyDefaults[d] || 0),
      0,
    );
    return tab === "daily"
      ? `~${total} ${metric.unit}/week`
      : tab === "weekly"
        ? `~${total * 4.3} ${metric.unit}/month`
        : `~${Math.round(total / 7)} ${metric.unit}/day avg`;
  })();

  return (
    <div className={`rounded-lg border p-3 ${sectionBg}`}>
      {/* Granularity tabs */}
      <div className="flex gap-1 mb-3 border-b border-gray-700/40 pb-2">
        {["daily", "weekly", "monthly"].map((g) => (
          <button
            key={g}
            onClick={() => setGranularity(g)}
            className={`px-3 py-1 text-xs font-medium rounded-t ${
              tab === g
                ? "bg-[#427BCA]/20 text-[#427BCA]"
                : isDarkMode
                  ? "text-gray-500 hover:text-gray-300"
                  : "text-gray-400"
            }`}
          >
            {g[0].toUpperCase() + g.slice(1)}
          </button>
        ))}
      </div>

      {/* periodType toggle */}
      <div className="flex items-center gap-2 mb-3">
        <span className={`text-xs ${mutedCls}`}>Mode:</span>
        <div className="flex gap-1">
          <button
            onClick={() => setPeriodType("weekday_pattern")}
            className={`text-xs px-2 py-1 rounded ${periodType === "weekday_pattern" ? "bg-[#427BCA]/20 text-[#427BCA]" : "text-gray-500"}`}
          >
            Daily pattern
          </button>
          <button
            onClick={() => setPeriodType("fixed")}
            className={`text-xs px-2 py-1 rounded ${periodType === "fixed" ? "bg-[#427BCA]/20 text-[#427BCA]" : "text-gray-500"}`}
          >
            Fixed total
          </button>
        </div>
        <span className={`ml-auto text-xs ${mutedCls} italic`}>{preview}</span>
      </div>

      {/* Daily weekday grid */}
      {periodType === "weekday_pattern" && (
        <>
          <div className="flex items-center justify-between mb-2">
            <span
              className={`text-xs font-semibold uppercase tracking-wider ${mutedCls}`}
            >
              {tab === "daily"
                ? "Daily targets"
                : tab === "weekly"
                  ? "Day weights"
                  : "Day pattern"}
            </span>
            <div className="flex gap-1">
              {[
                { label: "Weekdays", key: "weekdays" },
                { label: "Reset", key: "reset" },
              ].map(({ label, key }) => (
                <button
                  key={key}
                  onClick={() => applyQuickFill(key)}
                  className={`text-xs px-2 py-0.5 rounded border ${isDarkMode ? "border-gray-700 text-gray-400" : "border-gray-300 text-gray-500"}`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-7 gap-2">
            {DAYS.map((day) => {
              const val = metric.weeklyDefaults?.[day] || 0;
              return (
                <div key={day} className="text-center">
                  <div className={`text-[10px] mb-1 ${mutedCls}`}>
                    {DAY_SHORT[day]}
                  </div>
                  <input
                    type="number"
                    min="0"
                    max="999"
                    value={val}
                    onChange={(e) => updateDefault(day, e.target.value)}
                    className={`w-full text-center text-sm font-bold rounded border px-1 py-1.5 ${inputBg}`}
                  />
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Fixed value input */}
      {periodType === "fixed" && (
        <div>
          <label
            className={`text-xs font-semibold uppercase tracking-wider ${mutedCls}`}
          >
            Total for{" "}
            {tab === "daily"
              ? "each day"
              : tab === "weekly"
                ? "each week"
                : "each month"}
          </label>
          <input
            type="number"
            min="0"
            max="9999"
            value={metric.fixedValue ?? ""}
            onChange={(e) =>
              onChange({ ...metric, fixedValue: parseInt(e.target.value) || 0 })
            }
            className={`w-full mt-1 rounded border px-3 py-2 text-sm ${inputBg}`}
            placeholder="e.g. 10"
          />
          <p className={`text-xs mt-1 ${mutedCls}`}>
            {tab === "weekly" &&
              "Distributed automatically across working days (Mon–Fri weighted)."}
            {tab === "monthly" &&
              "Tracked as a single monthly number — daily progress rolls up."}
          </p>
        </div>
      )}
    </div>
  );
};
// ─── MetricPicker (slide-over panel) ─────────────────────────────────────────

const MetricPicker = ({
  teamType,
  onAdd,
  isDarkMode,
  existingMetricIds,
  onClose,
}) => {
  const [search, setSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");

  const categories = ["All", ...Object.keys(CATEGORY_META)];

  const available = METRIC_LIBRARY.filter((m) => {
    const matchesTeam =
      m.applicableTeamTypes.includes(teamType) ||
      teamType === "custom" ||
      !teamType;
    const matchesSearch =
      !search ||
      m.label.toLowerCase().includes(search.toLowerCase()) ||
      m.description.toLowerCase().includes(search.toLowerCase());
    const matchesCategory =
      selectedCategory === "All" || m.category === selectedCategory;
    return matchesTeam && matchesSearch && matchesCategory;
  });

  const cardBg = isDarkMode
    ? "bg-[#0D0F14] border-gray-700"
    : "bg-white border-gray-200";
  const mutedCls = isDarkMode ? "text-gray-500" : "text-gray-400";
  const hoverBg = isDarkMode ? "hover:bg-white/5" : "hover:bg-gray-50";
  const inputBg = isDarkMode
    ? "bg-[#1A1D23] border-gray-700 text-white placeholder-gray-600"
    : "bg-gray-100 border-gray-200 text-gray-900 placeholder-gray-400";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-end">
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      <div
        className={`relative h-full w-full max-w-md flex flex-col shadow-2xl border-l ${cardBg}`}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-700/50">
          <div>
            <h2
              className={`font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}
            >
              Add a metric
            </h2>
            <p className={`text-xs mt-0.5 ${mutedCls}`}>
              {available.length} available for this team type
            </p>
          </div>
          <button
            onClick={onClose}
            className={`p-2 rounded-lg transition-colors ${isDarkMode ? "hover:bg-white/10 text-gray-400" : "hover:bg-gray-100 text-gray-500"}`}
          >
            <X size={16} />
          </button>
        </div>

        {/* Search */}
        <div className="px-4 pt-4 pb-2">
          <div className="relative">
            <Search
              size={14}
              className={`absolute left-3 top-1/2 -translate-y-1/2 ${mutedCls}`}
            />
            <input
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search metrics…"
              className={`w-full pl-9 pr-3 py-2 text-sm rounded-lg border focus:outline-none focus:ring-1 focus:ring-[#427BCA] ${inputBg}`}
            />
          </div>
        </div>

        {/* Category tabs */}
        <div className="px-4 pb-3 flex gap-1.5 flex-wrap">
          {categories.map((cat) => {
            const meta = CATEGORY_META[cat];
            const isActive = selectedCategory === cat;
            return (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                className={`text-xs px-2.5 py-1 rounded-full border transition-all flex items-center gap-1 ${
                  isActive
                    ? "bg-[#427BCA]/20 border-[#427BCA] text-[#427BCA]"
                    : isDarkMode
                      ? "border-gray-700 text-gray-400 hover:border-gray-600"
                      : "border-gray-200 text-gray-500 hover:border-gray-300"
                }`}
              >
                {cat}
              </button>
            );
          })}
        </div>

        {/* Metric list */}
        <div className="flex-1 overflow-y-auto">
          {available.length === 0 && (
            <div className={`px-6 py-12 text-center ${mutedCls}`}>
              <Search size={24} className="mx-auto mb-2 opacity-40" />
              <p className="text-sm">No metrics match your search</p>
            </div>
          )}

          {/* Group by category */}
          {categories
            .filter(
              (c) =>
                c !== "All" &&
                (selectedCategory === "All" || selectedCategory === c),
            )
            .map((cat) => {
              const group = available.filter((m) => m.category === cat);
              if (group.length === 0) return null;
              const meta = CATEGORY_META[cat];
              const CatIcon = meta.icon;
              return (
                <div key={cat}>
                  <div
                    className={`px-4 py-2 flex items-center gap-2 border-b border-gray-700/30 ${isDarkMode ? "bg-white/[0.02]" : "bg-gray-50"}`}
                  >
                    <CatIcon size={12} className={meta.color} />
                    <span
                      className={`text-xs font-semibold uppercase tracking-wider ${mutedCls}`}
                    >
                      {cat}
                    </span>
                  </div>
                  {group.map((m) => {
                    const alreadyAdded = existingMetricIds.includes(m.id);
                    return (
                      <button
                        key={m.id}
                        disabled={alreadyAdded}
                        onClick={() => {
                          onAdd(m);
                          onClose();
                        }}
                        className={`w-full text-left px-4 py-3 flex items-start gap-3 border-b border-gray-700/20 transition-colors ${alreadyAdded ? "opacity-40 cursor-not-allowed" : hoverBg}`}
                      >
                        <span className="text-xl mt-0.5 flex-shrink-0">
                          {m.icon}
                        </span>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <p
                              className={`text-sm font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
                            >
                              {m.label}
                            </p>
                            {alreadyAdded && <Badge color="gray">Added</Badge>}
                          </div>
                          <p className="text-xs text-gray-500 mt-0.5">
                            {m.description}
                          </p>
                          <div className="flex gap-1 mt-1.5 flex-wrap">
                            {m.applicableTeamTypes.map((t) => (
                              <Badge
                                key={t}
                                color={t === teamType ? "blue" : "gray"}
                              >
                                {TEAM_TYPE_LABELS[t] || t}
                              </Badge>
                            ))}
                            {m.filters?.length > 0 && (
                              <Badge color="gray">
                                <Filter size={9} />
                                {m.filters.length} filter
                                {m.filters.length > 1 ? "s" : ""}
                              </Badge>
                            )}
                          </div>
                        </div>
                        {!alreadyAdded && (
                          <Plus
                            size={14}
                            className="text-[#427BCA] flex-shrink-0 mt-1"
                          />
                        )}
                      </button>
                    );
                  })}
                </div>
              );
            })}
        </div>
      </div>
    </div>
  );
};

// ─── PresetModal ──────────────────────────────────────────────────────────────

const PresetModal = ({ teamType, onApply, onClose, isDarkMode }) => {
  const cardBg = isDarkMode
    ? "bg-[#0D0F14] border-gray-700"
    : "bg-white border-gray-200";
  const mutedCls = isDarkMode ? "text-gray-500" : "text-gray-400";

  const relevant = PRESET_CONFIGS.filter(
    (p) =>
      !p.teamTypes ||
      p.teamTypes.some((t) => t === teamType) ||
      teamType === "custom",
  );
  const others = PRESET_CONFIGS.filter((p) => !relevant.includes(p));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />
      <div
        className={`relative w-full max-w-2xl rounded-2xl border shadow-2xl overflow-hidden ${cardBg}`}
      >
        <div className="flex items-center justify-between p-5 border-b border-gray-700/50">
          <div>
            <h2
              className={`font-semibold text-lg ${isDarkMode ? "text-white" : "text-gray-900"}`}
            >
              <Sparkles size={16} className="inline mr-2 text-[#427BCA]" />
              Quick-start templates
            </h2>
            <p className={`text-xs mt-0.5 ${mutedCls}`}>
              Choose a template and customise from there
            </p>
          </div>
          <button
            onClick={onClose}
            className={`p-2 rounded-lg transition-colors ${isDarkMode ? "hover:bg-white/10 text-gray-400" : "hover:bg-gray-100 text-gray-500"}`}
          >
            <X size={16} />
          </button>
        </div>

        <div className="p-4 space-y-4 max-h-[70vh] overflow-y-auto">
          {relevant.length > 0 && (
            <div>
              <p
                className={`text-xs font-semibold uppercase tracking-wider mb-2 ${mutedCls}`}
              >
                Recommended for {TEAM_TYPE_LABELS[teamType] || teamType}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {relevant.map((preset) => (
                  <PresetCard
                    key={preset.id}
                    preset={preset}
                    onApply={onApply}
                    isDarkMode={isDarkMode}
                    highlighted
                  />
                ))}
              </div>
            </div>
          )}

          {others.length > 0 && (
            <div>
              <p
                className={`text-xs font-semibold uppercase tracking-wider mb-2 ${mutedCls}`}
              >
                Other templates
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {others.map((preset) => (
                  <PresetCard
                    key={preset.id}
                    preset={preset}
                    onApply={onApply}
                    isDarkMode={isDarkMode}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const PresetCard = ({ preset, onApply, isDarkMode, highlighted }) => {
  const mutedCls = isDarkMode ? "text-gray-500" : "text-gray-400";
  return (
    <button
      onClick={() => onApply(preset)}
      className={`text-left p-4 rounded-xl border transition-all group ${
        highlighted
          ? isDarkMode
            ? "border-[#427BCA]/40 bg-[#427BCA]/5 hover:bg-[#427BCA]/10 hover:border-[#427BCA]/60"
            : "border-[#427BCA]/40 bg-blue-50 hover:bg-blue-100"
          : isDarkMode
            ? "border-gray-700 hover:border-gray-600 hover:bg-white/5"
            : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
      }`}
    >
      <div className="flex items-start gap-3">
        <span className="text-2xl">{preset.icon}</span>
        <div className="flex-1 min-w-0">
          <p
            className={`font-semibold text-sm ${isDarkMode ? "text-white" : "text-gray-900"}`}
          >
            {preset.name}
          </p>
          <p className={`text-xs mt-0.5 ${mutedCls}`}>{preset.description}</p>
          <div className="flex flex-wrap gap-1 mt-2">
            {preset.metrics.map((m) => {
              const def = METRIC_LIBRARY.find((d) => d.id === m.metricId);
              return (
                <span
                  key={m.metricId}
                  className={`text-xs px-1.5 py-0.5 rounded-md ${isDarkMode ? "bg-white/10 text-gray-400" : "bg-gray-100 text-gray-500"}`}
                >
                  {def?.icon} {m.label}
                </span>
              );
            })}
          </div>
        </div>
        <ChevronRight
          size={14}
          className={`text-gray-500 group-hover:text-[#427BCA] transition-colors mt-1 flex-shrink-0`}
        />
      </div>
    </button>
  );
};

// ─── Summary strip ────────────────────────────────────────────────────────────

const ConfigSummary = ({ metrics, teamName, isDarkMode }) => {
  const mutedCls = isDarkMode ? "text-gray-500" : "text-gray-400";
  const enabled = metrics.filter((m) => m.enabled);
  const primary = metrics.find((m) => m.primaryMetric);
  const totalWeeklyTargets = enabled.reduce((sum, m) => {
    return (
      sum +
      DAYS.filter((d) => m.weeklyDefaults[d] > 0).reduce(
        (s, d) => s + (m.weeklyDefaults[d] || 0),
        0,
      )
    );
  }, 0);

  const items = [
    {
      label: "Metrics",
      value: metrics.length,
      sub: `${enabled.length} active`,
    },
    {
      label: "Weekly total",
      value: totalWeeklyTargets,
      sub: "across all metrics",
    },
    {
      label: "Primary",
      value: primary?.icon || "—",
      sub: primary?.label || "none set",
    },
    {
      label: "Categories",
      value: [
        ...new Set(
          metrics
            .map(
              (m) => METRIC_LIBRARY.find((d) => d.id === m.metricId)?.category,
            )
            .filter(Boolean),
        ),
      ].length,
      sub: "types tracked",
    },
  ];

  return (
    <div className={`grid grid-cols-2 md:grid-cols-4 gap-3`}>
      {items.map((item) => (
        <div
          key={item.label}
          className={`rounded-xl border p-3 text-center ${isDarkMode ? "bg-[#0D0F14] border-gray-700/80" : "bg-white border-gray-200"}`}
        >
          <div
            className={`text-xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}
          >
            {item.value}
          </div>
          <div
            className={`text-xs font-medium mt-0.5 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
          >
            {item.label}
          </div>
          <div className={`text-[10px] mt-0.5 ${mutedCls}`}>{item.sub}</div>
        </div>
      ))}
    </div>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

const TeamPerformanceBuilder = () => {
  const { isDarkMode } = useTheme();

  const [teams, setTeams] = useState([]);
  const [selectedTeamId, setSelectedTeamId] = useState(null);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [metrics, setMetrics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [configLoading, setConfigLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [showPresets, setShowPresets] = useState(false);
  const [showPicker, setShowPicker] = useState(false);

  // Theming
  const pageBg = isDarkMode
    ? "bg-[#0A0B0F] text-white"
    : "bg-gray-50 text-gray-900";
  const cardBg = isDarkMode
    ? "bg-[#0D0F14] border-gray-700"
    : "bg-white border-gray-200";
  const mutedCls = isDarkMode ? "text-gray-500" : "text-gray-400";
  const labelCls = isDarkMode ? "text-gray-300" : "text-gray-700";
  const inputBg = isDarkMode
    ? "bg-[#1A1D23] border-gray-600 text-white"
    : "bg-white border-gray-300 text-gray-900";

  // ── Load teams ───────────────────────────────────────────────────────────────
  useEffect(() => {
    const load = async () => {
      try {
        const res = await apiService.getAllCompanyTeams();
        const relevant = (res.teams || []).filter((t) =>
          [
            "sales",
            "sales_engagement",
            "telemarketing",
            "support",
            "operations",
            "engineering",
            "custom",
          ].includes(t.type),
        );
        setTeams(relevant);
        if (relevant.length > 0 && !selectedTeamId)
          setSelectedTeamId(relevant[0].id);
      } catch {
        setError("Failed to load teams");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  useEffect(() => {
    if (!selectedTeamId || teams.length === 0) return;
    setSelectedTeam(teams.find((t) => t.id === selectedTeamId) || null);
  }, [selectedTeamId, teams]);

  // ── Load config ──────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!selectedTeamId) return;
    const loadConfig = async () => {
      setConfigLoading(true);
      setError(null);
      try {
        const res = await apiService.getTeamPerformanceConfig(selectedTeamId);
        const cfg = res.data;
        setMetrics(
          cfg?.metrics?.length > 0
            ? cfg.metrics.map((m) => ({
                ...m,
                id:
                  m.id ||
                  `metric_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
                category:
                  METRIC_LIBRARY.find((d) => d.id === m.metricId)?.category ||
                  "Activity",
              }))
            : [],
        );
        setDirty(false);
      } catch (err) {
        if (err?.response?.status === 404) setMetrics([]);
        else setError("Failed to load configuration");
      } finally {
        setConfigLoading(false);
      }
    };
    loadConfig();
  }, [selectedTeamId]);

  // ── Metric operations ────────────────────────────────────────────────────────
  const addMetric = useCallback((metricDef) => {
    const entry = makeMetricEntry(metricDef);
    setMetrics((prev) => [...prev, entry]);
    setDirty(true);
  }, []);

  const updateMetric = useCallback((updated) => {
    setMetrics((prev) => prev.map((m) => (m.id === updated.id ? updated : m)));
    setDirty(true);
  }, []);

  const removeMetric = useCallback((id) => {
    setMetrics((prev) => prev.filter((m) => m.id !== id));
    setDirty(true);
  }, []);

  const togglePrimary = useCallback((id) => {
    setMetrics((prev) =>
      prev.map((m) => ({
        ...m,
        primaryMetric: m.id === id ? !m.primaryMetric : false,
      })),
    );
    setDirty(true);
  }, []);

  const moveMetric = useCallback((index, direction) => {
    setMetrics((prev) => {
      const next = [...prev];
      const target = index + direction;
      if (target < 0 || target >= next.length) return prev;
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
    setDirty(true);
  }, []);

  const applyPreset = (preset) => {
    const newMetrics = preset.metrics.map((m) => ({
      ...m,
      id: `metric_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
      icon: METRIC_LIBRARY.find((d) => d.id === m.metricId)?.icon || "📊",
      unit: METRIC_LIBRARY.find((d) => d.id === m.metricId)?.unit || "items",
      queryType:
        METRIC_LIBRARY.find((d) => d.id === m.metricId)?.queryType ||
        m.metricId,
      category:
        METRIC_LIBRARY.find((d) => d.id === m.metricId)?.category || "Activity",
      filters: m.filters || {},
    }));
    setMetrics(newMetrics);
    setShowPresets(false);
    setDirty(true);
  };

  // ── Save ─────────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const payload = metrics.map((m) => ({
        ...m,
        granularity: m.granularity || "daily",
        periodType: m.periodType || "weekday_pattern",
      }));
      await apiService.updateTeamPerformanceConfig(selectedTeamId, {
        metrics: payload,
      });
      setSaved(true);
      setDirty(false);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      setError(e?.response?.data?.message || "Failed to save configuration");
    } finally {
      setSaving(false);
    }
  };

  const existingMetricIds = metrics.map((m) => m.metricId);

  // ── Render ───────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div
        className={`min-h-screen flex items-center justify-center ${pageBg}`}
      >
        <RefreshCw className="animate-spin text-[#427BCA]" size={28} />
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${pageBg}`}>
      <div className="max-w-5xl mx-auto p-6 space-y-5">
        {/* ── Header ── */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1
              className={`text-2xl font-bold flex items-center gap-3 ${isDarkMode ? "text-white" : "text-gray-900"}`}
            >
              <div className="p-2 rounded-xl bg-[#427BCA]/15">
                <Layers className="text-[#427BCA]" size={22} />
              </div>
              Performance Metric Builder
            </h1>
            <p className={`mt-1 text-sm ${mutedCls}`}>
              Configure what each team is judged on and set their daily targets.
              Metrics, filters and targets are fully independent per team.
            </p>
          </div>
        </div>

        {/* ── Error ── */}
        {error && (
          <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
            <AlertCircle size={16} className="flex-shrink-0" />
            <span>{error}</span>
            <button
              onClick={() => setError(null)}
              className="ml-auto text-red-400/60 hover:text-red-400"
            >
              <X size={14} />
            </button>
          </div>
        )}

        {/* ── Team selector ── */}
        <div className={`rounded-xl border p-4 ${cardBg}`}>
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <div className="p-2 rounded-lg bg-[#427BCA]/10">
                <Users size={16} className="text-[#427BCA]" />
              </div>
              <div className="flex-1 min-w-0">
                <p
                  className={`text-xs font-semibold uppercase tracking-wider mb-1 ${mutedCls}`}
                >
                  Configuring team
                </p>
                <select
                  value={selectedTeamId || ""}
                  onChange={(e) => {
                    if (
                      dirty &&
                      !window.confirm(
                        "You have unsaved changes. Switch team anyway?",
                      )
                    )
                      return;
                    setSelectedTeamId(e.target.value);
                  }}
                  className={`w-full text-sm font-medium rounded-lg border px-3 py-2 focus:outline-none focus:ring-1 focus:ring-[#427BCA] transition-colors ${inputBg}`}
                >
                  {teams.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name} — {TEAM_TYPE_LABELS[t.type] || t.type}
                      {t.members ? ` (${t.members.length} members)` : ""}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {selectedTeam && (
              <div className="flex items-center gap-2 flex-shrink-0">
                <Badge color={selectedTeam.isActive ? "emerald" : "red"}>
                  {selectedTeam.isActive ? "Active" : "Inactive"}
                </Badge>
                <Badge color="gray">
                  {TEAM_TYPE_LABELS[selectedTeam.type]}
                </Badge>
                {selectedTeam.members && (
                  <Badge color="gray">
                    <Users size={9} />
                    {selectedTeam.members.length}
                  </Badge>
                )}
              </div>
            )}
          </div>
        </div>

        {configLoading ? (
          <div className="flex items-center justify-center py-20">
            <RefreshCw className="animate-spin text-[#427BCA]" size={22} />
          </div>
        ) : (
          selectedTeam && (
            <>
              {/* ── Summary ── */}
              {metrics.length > 0 && (
                <ConfigSummary
                  metrics={metrics}
                  teamName={selectedTeam.name}
                  isDarkMode={isDarkMode}
                />
              )}

              {/* ── Toolbar ── */}
              <div className="flex items-center gap-3 flex-wrap">
                <button
                  onClick={() => setShowPicker(true)}
                  className="flex items-center gap-2 px-4 py-2 rounded-xl bg-[#427BCA]/15 border border-[#427BCA]/30 text-[#427BCA] hover:bg-[#427BCA]/25 transition-all text-sm font-medium"
                >
                  <Plus size={15} />
                  Add metric
                </button>
                <button
                  onClick={() => setShowPresets(true)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl border text-sm font-medium transition-colors ${isDarkMode ? "border-gray-700 text-gray-300 hover:border-gray-600 hover:bg-white/5" : "border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50"}`}
                >
                  <Sparkles size={15} />
                  Templates
                </button>

                {metrics.length > 0 && (
                  <button
                    onClick={() => {
                      if (window.confirm("Clear all metrics for this team?")) {
                        setMetrics([]);
                        setDirty(true);
                      }
                    }}
                    className="ml-auto flex items-center gap-2 px-3 py-2 rounded-xl border text-sm font-medium transition-colors text-red-400/70 border-red-500/20 hover:bg-red-500/10 hover:text-red-400 hover:border-red-500/40"
                  >
                    <Trash2 size={14} />
                    Clear all
                  </button>
                )}
              </div>

              {/* ── Empty state ── */}
              {metrics.length === 0 && (
                <div
                  className={`rounded-xl border-2 border-dashed p-14 text-center ${isDarkMode ? "border-gray-700 text-gray-600" : "border-gray-300 text-gray-400"}`}
                >
                  <div className="w-14 h-14 rounded-2xl bg-[#427BCA]/10 flex items-center justify-center mx-auto mb-4">
                    <Target size={28} className="text-[#427BCA] opacity-60" />
                  </div>
                  <p
                    className={`font-semibold mb-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    No metrics configured yet
                  </p>
                  <p className="text-sm mb-5">
                    Start with a template for quick setup, or add metrics
                    individually.
                  </p>
                  <div className="flex justify-center gap-3">
                    <button
                      onClick={() => setShowPresets(true)}
                      className="text-sm px-4 py-2 rounded-xl bg-[#427BCA]/15 text-[#427BCA] hover:bg-[#427BCA]/25 transition-colors font-medium"
                    >
                      <Sparkles size={13} className="inline mr-1.5" />
                      Browse templates
                    </button>
                    <button
                      onClick={() => setShowPicker(true)}
                      className={`text-sm px-4 py-2 rounded-xl border font-medium transition-colors ${isDarkMode ? "border-gray-700 text-gray-400 hover:border-gray-600 hover:bg-white/5" : "border-gray-200 text-gray-600 hover:bg-gray-50"}`}
                    >
                      <Plus size={13} className="inline mr-1.5" />
                      Add manually
                    </button>
                  </div>
                </div>
              )}

              {/* ── Metric cards ── */}
              <div className="space-y-3">
                {metrics.map((metric, idx) => (
                  <MetricCard
                    key={metric.id}
                    metric={metric}
                    index={idx}
                    total={metrics.length}
                    onUpdate={updateMetric}
                    onRemove={removeMetric}
                    onTogglePrimary={togglePrimary}
                    onMoveUp={(i) => moveMetric(i, -1)}
                    onMoveDown={(i) => moveMetric(i, 1)}
                    isDarkMode={isDarkMode}
                  />
                ))}
              </div>

              {/* ── How it works ── */}
              {metrics.length > 0 && (
                <div
                  className={`rounded-xl border p-5 ${isDarkMode ? "bg-[#0D0F14] border-gray-800" : "bg-gray-50 border-gray-200"}`}
                >
                  <h3
                    className={`text-sm font-semibold mb-3 flex items-center gap-2 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                  >
                    <Info size={14} className="text-[#427BCA]" />
                    How it works for team members
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
                    {[
                      {
                        icon: "📊",
                        title: "Daily dashboard",
                        body: `Members see their ${
                          metrics
                            .filter((m) => m.primaryMetric)
                            .map((m) => m.label)
                            .join(", ") ||
                          metrics[0]?.label ||
                          "primary metric"
                        } progress each day with live actuals vs. targets.`,
                      },
                      {
                        icon: "🎯",
                        title: "Flexible targets",
                        body: "Weekly default targets are used unless a manager sets a member-specific override. Days set to 0 are treated as rest days and excluded from scoring.",
                      },
                      {
                        icon: "🔍",
                        title: "Filtered actuals",
                        body: "Filters on each metric narrow which records count — e.g. only 'New Business' appointments, or specific case types. Unfiltered metrics count everything.",
                      },
                    ].map((item) => (
                      <div
                        key={item.title}
                        className={`p-3 rounded-lg ${isDarkMode ? "bg-white/5" : "bg-white border border-gray-100"}`}
                      >
                        <div className="text-lg mb-1.5">{item.icon}</div>
                        <p
                          className={`font-semibold mb-1 ${isDarkMode ? "text-gray-200" : "text-gray-800"}`}
                        >
                          {item.title}
                        </p>
                        <p className={mutedCls}>{item.body}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* ── Sticky save bar ── */}
              {metrics.length > 0 && (
                <div
                  className={`sticky bottom-4 rounded-xl border p-4 flex items-center justify-between gap-4 shadow-2xl backdrop-blur-sm z-30 ${isDarkMode ? "bg-[#0D0F14]/95 border-gray-700" : "bg-white/95 border-gray-200"}`}
                >
                  <div>
                    <p
                      className={`text-sm font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
                    >
                      {metrics.length} metric{metrics.length !== 1 ? "s" : ""}{" "}
                      configured for{" "}
                      <span className="text-[#427BCA]">
                        {selectedTeam.name}
                      </span>
                    </p>
                    <p className={`text-xs mt-0.5 ${mutedCls}`}>
                      {metrics.filter((m) => m.enabled).length} active ·{" "}
                      {metrics.filter((m) => !m.enabled).length} paused ·{" "}
                      {metrics.filter((m) => m.primaryMetric).length} primary
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    {dirty && (
                      <span className="text-xs text-yellow-400 flex items-center gap-1">
                        <AlertCircle size={12} />
                        Unsaved changes
                      </span>
                    )}
                    {saved && (
                      <span className="text-xs text-green-400 flex items-center gap-1">
                        <CheckCircle2 size={12} />
                        Saved
                      </span>
                    )}
                    <button
                      onClick={handleSave}
                      disabled={saving || !dirty}
                      className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#427BCA] text-white hover:bg-[#3569b0] transition-colors disabled:opacity-40 disabled:cursor-not-allowed text-sm font-medium"
                    >
                      {saving ? (
                        <RefreshCw size={14} className="animate-spin" />
                      ) : (
                        <Save size={14} />
                      )}
                      Save configuration
                    </button>
                  </div>
                </div>
              )}
            </>
          )
        )}
      </div>

      {/* ── Modals ── */}
      {showPicker && (
        <MetricPicker
          teamType={selectedTeam?.type}
          onAdd={addMetric}
          isDarkMode={isDarkMode}
          existingMetricIds={existingMetricIds}
          onClose={() => setShowPicker(false)}
        />
      )}

      {showPresets && (
        <PresetModal
          teamType={selectedTeam?.type}
          onApply={(preset) => {
            if (
              metrics.length === 0 ||
              window.confirm(
                "This will replace your current metrics. Continue?",
              )
            ) {
              applyPreset(preset);
            }
          }}
          onClose={() => setShowPresets(false)}
          isDarkMode={isDarkMode}
        />
      )}
    </div>
  );
};

export default TeamPerformanceBuilder;
